"use client";

import { useEffect, useState } from "react";
import { Play, Workflow, Database, Slack, Github, Cloud, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: metricRef,
    isVisible: metricVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const [count, setCount] = useState(0);
  const target = 95.0;
  useEffect(() => {
    if (!metricVisible) return;
    const duration = 1400;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(parseFloat((eased * target).toFixed(1)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [metricVisible]);
  const integrations = [{
    name: "Slack",
    Icon: Slack
  }, {
    name: "GitHub",
    Icon: Github
  }, {
    name: "Cloud",
    Icon: Cloud
  }, {
    name: "Database",
    Icon: Database
  }, {
    name: "Workflow",
    Icon: Workflow
  }, {
    name: "Zap",
    Icon: Zap
  }];
  return <section data-section-id="6703" id="hero" className="bg-background text-foreground relative overflow-hidden py-32 md:py-44">
      <div className="mx-auto max-w-[1280px] px-5 md:px-8">
        {}
        <div ref={headRef} className="mx-auto max-w-[960px] text-center transition-all duration-[520ms] ease-out" style={{
        opacity: headVisible ? 1 : 0,
        transform: headVisible ? "translateY(0)" : "translateY(16px)"
      }}>
          {}
          <div className="mb-8 inline-flex items-center gap-2 rounded-md border border-border bg-card/60 px-3 py-1.5">
            <span className="inline-block size-1.5 rounded-md bg-primary" />
            <span className="text-[0.75rem] font-medium uppercase text-muted-foreground" style={{
            letterSpacing: "0.14em"
          }}>
              Enterprise Cloud Solutions
            </span>
          </div>

          {}
          <h1 className="font-default text-foreground" style={{
          fontSize: "clamp(2.75rem, 6.4vw, 5.5rem)",
          fontWeight: 560,
          letterSpacing: "-0.035em",
          lineHeight: 0.98
        }}>
            Scale your infrastructure without{" "}
            <span className="relative inline-block">
              <em className="not-italic" style={{
              fontStyle: "italic",
              fontWeight: 500,
              letterSpacing: "-0.04em"
            }}>
                complexity
              </em>
              <span aria-hidden="true" className="absolute left-0 bg-primary" style={{
              bottom: "-0.12em",
              height: "2px",
              width: headVisible ? "100%" : "0%",
              transition: "width 600ms ease-out 400ms"
            }} />
            </span>
          </h1>

          {}
          <p className="mx-auto mt-8 max-w-[720px] text-muted-foreground" style={{
          fontSize: "clamp(1.0625rem, 1.1vw, 1.1875rem)",
          lineHeight: 1.55,
          letterSpacing: "-0.005em"
        }}>
            Deploy, manage, and optimize your global cloud footprint with automated precision and enterprise-grade security.
          </p>

          {}
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <Link to="/GetInTouchPage">
              <Button size="lg" className="h-12 rounded-md bg-primary px-6 text-primary-foreground hover:bg-primary/90" style={{
              fontWeight: 520
            }}>
                Request a demo
              </Button>
            </Link>
            <Link to="/OurServicesPage">
              <Button size="lg" variant="outline" className="h-12 rounded-md border-border bg-transparent px-6 text-foreground hover:bg-card hover:text-foreground" style={{
              fontWeight: 520
            }}>
                <Play className="size-3.5 fill-current" />
                Explore our services
              </Button>
            </Link>
          </div>
        </div>

        {}
        <div className="mt-20 grid grid-cols-1 gap-5 md:gap-6 lg:grid-cols-12">
          {}
          <div ref={leftRef} className="lg:col-span-8 transition-all duration-[520ms] ease-out" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(16px)",
          transitionDelay: "60ms"
        }}>
            <div className="group relative h-full overflow-hidden rounded-2xl border border-border bg-card p-2 shadow-2xl">
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-xl bg-background">
                {}
                <div className="absolute inset-0">
                  {}
                  <div className="absolute inset-0 opacity-40" style={{
                  backgroundImage: "radial-gradient(circle, hsl(222 12% 22%) 1px, transparent 1px)",
                  backgroundSize: "22px 22px"
                }} />
                  {}
                  <div className="absolute left-0 right-0 top-0 flex items-center justify-between border-b border-border bg-card/80 px-4 py-2.5 backdrop-blur-sm">
                    <div className="flex items-center gap-1.5">
                      <span className="size-2.5 rounded-md bg-muted" />
                      <span className="size-2.5 rounded-md bg-muted" />
                      <span className="size-2.5 rounded-md bg-muted" />
                    </div>
                    <div className="font-mono text-[0.6875rem] text-muted-foreground" style={{
                    letterSpacing: "0.04em"
                  }}>
                      cloud-ops / production-cluster
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-md border border-primary/30 bg-primary/10 px-2 py-0.5 text-[0.6875rem] font-medium text-primary">
                        Active
                      </span>
                    </div>
                  </div>

                  {}
                  <div className="absolute inset-0 flex items-center justify-center px-6 pt-12 sm:px-10">
                    <div className="flex w-full items-center justify-between gap-3 sm:gap-6">
                      {}
                      <div className="flex-1 rounded-xl border border-border bg-card p-3 sm:p-4 transition-all duration-[520ms]" style={{
                      opacity: leftVisible ? 1 : 0,
                      transform: leftVisible ? "translateY(0)" : "translateY(8px)",
                      transitionDelay: "200ms"
                    }}>
                        <div className="mb-2 flex items-center gap-1.5">
                          <Zap className="size-3 text-primary" />
                          <span className="font-mono text-[0.625rem] uppercase text-muted-foreground" style={{
                          letterSpacing: "0.12em"
                        }}>
                            Monitor
                          </span>
                        </div>
                        <div className="text-xs font-medium text-foreground sm:text-sm">
                          Real-time metrics
                        </div>
                        <div className="mt-1 text-[0.6875rem] text-muted-foreground">
                          Latency & Uptime
                        </div>
                      </div>

                      {}
                      <div className="relative hidden h-px flex-1 bg-border sm:block">
                        <div className="absolute inset-y-0 left-0 bg-primary" style={{
                        width: leftVisible ? "100%" : "0%",
                        transition: "width 900ms ease-out 400ms"
                      }} />
                      </div>

                      {}
                      <div className="flex-1 rounded-xl border border-border bg-card p-3 sm:p-4 transition-all duration-[520ms]" style={{
                      opacity: leftVisible ? 1 : 0,
                      transform: leftVisible ? "translateY(0)" : "translateY(8px)",
                      transitionDelay: "400ms"
                    }}>
                        <div className="mb-2 flex items-center gap-1.5">
                          <Database className="size-3 text-foreground" />
                          <span className="font-mono text-[0.625rem] uppercase text-muted-foreground" style={{
                          letterSpacing: "0.12em"
                        }}>
                            Analyze
                          </span>
                        </div>
                        <div className="text-xs font-medium text-foreground sm:text-sm">
                          Resource usage
                        </div>
                        <div className="mt-1 text-[0.6875rem] text-muted-foreground">
                          Auto-scaling logs
                        </div>
                      </div>

                      {}
                      <div className="relative hidden h-px flex-1 bg-border sm:block">
                        <div className="absolute inset-y-0 left-0 bg-primary" style={{
                        width: leftVisible ? "100%" : "0%",
                        transition: "width 900ms ease-out 900ms"
                      }} />
                      </div>

                      {}
                      <div className="flex-1 rounded-xl border border-primary/40 bg-card p-3 shadow-lg sm:p-4 transition-all" style={{
                      opacity: leftVisible ? 1 : 0,
                      transform: leftVisible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.96)",
                      transition: "opacity 900ms ease-out 900ms, transform 240ms cubic-bezier(0.22,1,0.36,1) 900ms",
                      boxShadow: leftVisible ? "0 0 0 3px hsl(199 89% 48% / 0.15)" : "none"
                    }}>
                        <div className="mb-2 flex items-center gap-1.5">
                          <Slack className="size-3 text-primary" />
                          <span className="font-mono text-[0.625rem] uppercase text-primary" style={{
                          letterSpacing: "0.12em"
                        }}>
                            Optimize
                          </span>
                        </div>
                        <div className="text-xs font-medium text-foreground sm:text-sm">
                          Auto-remediation
                        </div>
                        <div className="mt-1 text-[0.6875rem] text-muted-foreground">
                          Cost efficiency
                        </div>
                      </div>
                    </div>
                  </div>

                  {}
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between border-t border-border bg-card/80 px-4 py-2 backdrop-blur-sm">
                    <div className="font-mono text-[0.6875rem] text-muted-foreground" style={{
                    letterSpacing: "0.04em"
                  }}>
                      99.99% SLA
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="size-1.5 rounded-md bg-primary" />
                      <span className="text-[0.6875rem] text-muted-foreground">
                        Optimized
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className="flex flex-col gap-5 md:gap-6 lg:col-span-4 transition-all duration-[520ms] ease-out" style={{
          opacity: rightVisible ? 1 : 0,
          transform: rightVisible ? "translateY(0)" : "translateY(16px)",
          transitionDelay: "120ms"
        }}>
            {}
            <div ref={metricRef} className="flex-1 rounded-2xl border border-border bg-card p-8">
              <div className="mb-6 font-mono text-[0.75rem] uppercase text-muted-foreground" style={{
              letterSpacing: "0.14em"
            }}>
                Infrastructure efficiency
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-default text-primary" style={{
                fontSize: "clamp(3rem, 5vw, 4.5rem)",
                fontWeight: 560,
                letterSpacing: "-0.035em",
                lineHeight: 0.98
              }}>
                  {count.toFixed(1)}%
                </span>
              </div>
              <p className="mt-4 text-card-foreground/80" style={{
              fontSize: "0.9375rem",
              lineHeight: 1.55
            }}>
                Average resource utilization improvement for our enterprise partners.
              </p>
            </div>

            {}
            <div className="flex-1 rounded-2xl border border-border bg-card p-8">
              <div className="mb-6 font-mono text-[0.75rem] uppercase text-muted-foreground" style={{
              letterSpacing: "0.14em"
            }}>
                Multi-cloud support
              </div>
              <div className="grid grid-cols-6 gap-3">
                {integrations.map((item, i) => {
                const ItemIcon = item.Icon;
                return <div key={item.name} data-index={i} className="flex aspect-square items-center justify-center rounded-md border border-border/60 bg-background/40 text-foreground/50 transition-colors duration-200 hover:text-foreground" aria-label={item.name}>
                      <ItemIcon className="size-4" strokeWidth={1.5} />
                    </div>;
              })}
              </div>
              <p className="mt-5 text-card-foreground/70" style={{
              fontSize: "0.8125rem",
              lineHeight: 1.4
            }}>
                Seamless connectivity across AWS, Azure, GCP, and private clouds.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}